import { useState, useEffect, useRef } from "react";
import { Search, Loader2, Disc3, Music, LifeBuoy, ArrowUpRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

/**
 * Debounced search across the signed-in user's own releases, tracks and tickets.
 * `goto(section)` jumps between dashboard tabs (Releases / Support).
 */
export default function HomeSearch({ goto }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({ releases: [], tracks: [], tickets: [] });
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResults({ releases: [], tracks: [], tickets: [] });
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/home/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
      } catch (e) {
        /* ignore */
      } finally {
        setBusy(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const totalCount = results.releases.length + results.tracks.length + results.tickets.length;

  const Row = ({ testId, icon: Icon, title, subtitle, cover, onClick }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.04] transition-colors"
    >
      {cover ? (
        <img src={mediaUrl(cover)} alt="" className="h-9 w-9 rounded-md object-cover shrink-0" />
      ) : (
        <div className="h-9 w-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
          <Icon size={15} className="text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
      </div>
      <ArrowUpRight size={13} className="text-slate-500 shrink-0" />
    </button>
  );

  return (
    <div ref={boxRef} className="relative w-full" data-testid="home-search">
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 focus-within:border-blue-500/50 transition-colors">
        <Search size={16} className="text-slate-400 shrink-0" />
        <input
          data-testid="home-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search your releases, tracks, tickets…"
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
        />
        {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
        <kbd className="hidden md:inline-block text-[10px] tracking-[0.15em] font-medium text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
          Type to search
        </kbd>
      </div>
      {open && q.trim().length >= 2 && (
        <div
          data-testid="home-search-dropdown"
          className="absolute z-40 mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1220] shadow-2xl overflow-hidden"
        >
          {totalCount === 0 && !busy && (
            <div className="px-5 py-6 text-sm text-slate-500 text-center">No matches for &quot;{q}&quot;.</div>
          )}
          {results.releases.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Releases</div>
              {results.releases.map((r) => (
                <Row
                  key={`rel-${r.release_id}`}
                  testId={`home-search-release-${r.release_id}`}
                  icon={Disc3}
                  cover={r.cover_url}
                  title={r.title}
                  subtitle={[r.type, r.display_artist, r.upc && `UPC ${r.upc}`, r.status].filter(Boolean).join(" · ")}
                  onClick={() => { setOpen(false); if (typeof goto === "function") goto("releases"); }}
                />
              ))}
            </div>
          )}
          {results.tracks.length > 0 && (
            <div className="py-2 border-t border-white/5">
              <div className="px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Tracks</div>
              {results.tracks.map((t) => (
                <Row
                  key={`trk-${t.track_id}`}
                  testId={`home-search-track-${t.track_id}`}
                  icon={Music}
                  title={t.title}
                  subtitle={[t.display_artist, t.isrc && `ISRC ${t.isrc}`].filter(Boolean).join(" · ")}
                  onClick={() => { setOpen(false); if (typeof goto === "function") goto("releases"); }}
                />
              ))}
            </div>
          )}
          {results.tickets.length > 0 && (
            <div className="py-2 border-t border-white/5">
              <div className="px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Support tickets</div>
              {results.tickets.map((t) => (
                <Row
                  key={`tkt-${t.ticket_id}`}
                  testId={`home-search-ticket-${t.ticket_id}`}
                  icon={LifeBuoy}
                  title={t.title}
                  subtitle={[t.category_label, t.status].filter(Boolean).join(" · ")}
                  onClick={() => { setOpen(false); if (typeof goto === "function") goto("support"); }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
